
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Zap, Leaf, DollarSign, TrendingUp, Coins, Wrench } from 'lucide-react';
import ValorisationPageLayout from '@/components/valorisation/ValorisationPageLayout';
import ValorisationContentSection from '@/components/valorisation/ValorisationContentSection';
import MetauxNonFerreuxSEO from '@/components/valorisation/MetauxNonFerreuxSEO';

const MetauxNonFerreux = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Recyclage et Rachat des Métaux Non Ferreux",
    "description": "Service spécialisé dans le recyclage des métaux non ferreux : aluminium, cuivre, zinc, laiton. Rachat au meilleur prix.",
    "provider": {
      "@type": "Organization",
      "name": "Groupe Fabre Environnement",
      "url": "https://www.groupefabre-environnement.fr"
    },
    "areaServed": ["Occitanie", "Auvergne-Rhône-Alpes", "Île-de-France"],
    "serviceType": "Recyclage métaux non ferreux",
    "offers": {
      "@type": "Offer",
      "description": "Rachat et recyclage de tous types de métaux non ferreux"
    }
  };

  const breadcrumbItems = [
    { label: "Accueil", href: "/" },
    { label: "Valorisation", href: "/valorisation" },
    { label: "Métaux non ferreux", href: "/valorisation/metaux-non-ferreux", current: true }
  ];

  const contentBlocks = [
    {
      type: 'text' as const,
      title: "Les métaux non ferreux : des ressources précieuses",
      content: "Les métaux non ferreux sont des matériaux métalliques qui ne contiennent pas de fer comme élément principal. Contrairement aux métaux ferreux, ils ne présentent généralement pas de propriétés magnétiques et offrent souvent une meilleure résistance à la corrosion. Leur recyclage est essentiel pour l'économie circulaire et la préservation des ressources naturelles.",
      variant: 'highlighted' as const
    },
    {
      type: 'list' as const,
      title: "Principaux métaux non ferreux recyclés",
      items: [
        "Aluminium : canettes, profilés, radiateurs, emballages",
        "Cuivre : câbles électriques, tuyauteries, composants électroniques",
        "Zinc : gouttières, toitures, pièces galvanisées",
        "Laiton : robinetterie, décorations, instruments",
        "Plomb : batteries, tuyaux, plaques, contrepoids",
        "Inox : éviers, équipements industriels, chutes de fabrication",
        "Étain : soudures, alliages, conserves",
        "Nickel : batteries, alliages spéciaux, monnaies"
      ]
    },
    {
      type: 'process' as const,
      title: "Notre expertise en recyclage des métaux non ferreux",
      process: [
        {
          step: 1,
          title: "Identification et tri",
          description: "Identification précise des différents types de métaux non ferreux grâce à nos équipements spécialisés"
        },
        {
          step: 2,
          title: "Séparation avancée",
          description: "Utilisation de technologies de pointe pour séparer efficacement chaque type de métal"
        },
        {
          step: 3,
          title: "Préparation optimale",
          description: "Nettoyage et préparation des métaux pour maximiser leur valeur de revente"
        },
        {
          step: 4,
          title: "Valorisation maximale",
          description: "Acheminement vers les filières de recyclage appropriées pour une valorisation optimale"
        },
        {
          step: 5,
          title: "Nouvelle vie",
          description: "Transformation en nouveaux produits sans perte de qualité grâce au recyclage"
        }
      ]
    },
    {
      type: 'benefits' as const,
      title: "Pourquoi recycler les métaux non ferreux ?",
      benefits: [
        {
          title: "Économie d'énergie exceptionnelle",
          description: "Jusqu'à 95% d'économie d'énergie pour l'aluminium par rapport à la production primaire",
          icon: Zap
        },
        {
          title: "Préservation des ressources",
          description: "Réduction drastique de l'extraction minière et préservation des écosystèmes",
          icon: Leaf
        },
        {
          title: "Haute valeur économique",
          description: "Métaux précieux avec une valeur marchande élevée, rentabilité garantie",
          icon: DollarSign
        }
      ],
      variant: 'success' as const
    },
    {
      type: 'cta' as const,
      title: "Maximisez la valeur de vos métaux non ferreux",
      content: "Nos experts évaluent gratuitement vos métaux non ferreux et vous proposent les meilleurs tarifs du marché avec une traçabilité complète.",
      ctaText: "Obtenir une estimation gratuite",
      ctaLink: "/estimation-metaux",
      variant: 'highlighted' as const
    }
  ];

  return (
    <ValorisationPageLayout
      title="Recyclage et Rachat des Métaux Non Ferreux | Groupe Fabre Environnement"
      description="Valorisez vos métaux non ferreux avec le Groupe Fabre Environnement. Services de collecte, rachat et recyclage pour professionnels et particuliers."
      canonical="/valorisation/metaux-non-ferreux"
      keywords="recyclage métaux non ferreux, rachat aluminium, cuivre, zinc, laiton, valorisation métaux précieux"
      structuredData={structuredData}
      heroTitle="Métaux Non Ferreux"
      heroSubtitle="Recyclage du cuivre, aluminium, zinc et autres métaux précieux"
      breadcrumbItems={breadcrumbItems}
    >
      <ValorisationContentSection
        title="Recyclage spécialisé des métaux non ferreux"
        subtitle="Expertise reconnue dans la valorisation des métaux précieux et non magnétiques"
        blocks={contentBlocks}
        className="bg-white"
      />
      
      <MetauxNonFerreuxSEO />
    </ValorisationPageLayout>
  );
};

export default MetauxNonFerreux;
